import cloneDeep from 'lodash/cloneDeep';
import mergeObjects from '../../util/mergeObjects';
import {
  Ecdn11Block,
  GeneralBlock,
  Report
} from '../../reporter/report/Report';
import { EventReport } from './events/interfaces';
import DataReportBlock from '../../reporter/report/DataReportBlock';

export const MAX_RECORDS = 200;

/**
 * Merges all DataReportBlocks for a list of reports, then trims them so that
 * we don't have more than MAX_RECORDS of any field (uses First In First Out).
 * @param key - report field name
 * @param reports - list of reports to mergeDataReportBlock
 * @param filterFunc - optional filter to remove duplicate entries
 */
export function mergeDataReportBlock<T>(
  key: keyof Report,
  reports: Report[],
  out: Report,
  filterFunc?: FilterFunction<T>
): void {
  const accumulator = new DataReportBlock<T>();
  reports.forEach(report => {
    const src = report[key] as unknown as DataReportBlock<T>;
    if (src?.data && src.data.length > 0) {
      accumulator.data.push(...src.data);
    }
  });
  trimList(accumulator, filterFunc);
  (out[key] as DataReportBlock<T>) = accumulator;
}

export const filterEvent = (
  it: EventReport,
  filter: Record<string, boolean>
): boolean => {
  if (it.e != 4) {
    // 4 is a durationchange event.
    return false;
  }
  if (filter.durationchange) {
    return true;
  }
  filter.durationchange = true;
  return false;
};

export type FilterFunction<T> = (
  element: T,
  filter: Record<string, boolean>
) => boolean;

/**
 * Alters DataReportBlock in place
 * @param block - the data report block to trim
 * @param filter - optional filter to filter out duplicate or unneeded items
 */
export function trimList<T>(
  block?: DataReportBlock<T>,
  filter?: FilterFunction<T>
): void {
  if (!block) {
    return;
  }
  const filterState: Record<string, boolean> = {};
  // Reversed so that we'll keep the last items.
  const input: T[] = [...block.data].reverse();
  block.data.length = 0;
  for (const it of input) {
    if (block.data.length >= MAX_RECORDS) {
      return;
    }
    if (filter && filter(it, filterState)) {
      continue;
    }
    block.data.unshift(it);
  }
}

export function mergeReports(reports: Report[], report?: Report): Report {
  const out = report ?? new Report();
  mergeDataReportBlock('attachmentRequests', reports, out);
  mergeDataReportBlock('bitrates', reports, out);
  mergeDataReportBlock('connections', reports, out);
  mergeDataReportBlock('contentNotifications', reports, out);
  mergeDataReportBlock('detachments', reports, out);
  mergeDataReportBlock('detection', reports, out);
  mergeDataReportBlock('events', reports, out, filterEvent);
  mergeDataReportBlock('log', reports, out);
  mergeDataReportBlock('nodeState', reports, out);
  mergeDataReportBlock('peerNotifications', reports, out);
  mergeDataReportBlock('peerRequests', reports, out);
  mergeDataReportBlock('priming', reports, out);
  mergeDataReportBlock('transfers', reports, out);

  out.ecdn11 = new Ecdn11Block(
    mergeObjects(
      reports.map(r => r.ecdn11),
      true
    )
  );
  out.general = new GeneralBlock(
    mergeObjects(
      reports.map(r => r.general),
      true
    )
  );
  return out;
}

export default function mergeReport(merged: Report, pending: Report): Report {
  pending = cloneDeep(pending);

  delete pending.general.t;
  delete pending.general.critical;
  delete pending.general.expedited;

  return mergeReports([merged, pending]);
}
